import { ensureCustomFilterStyle } from './filterStyles';

/**
 * 건수 범위 필터
 * 사용 예: { filter: CountRangeFilter }
 */
class CountRangeFilter {
  init(params) {
    ensureCustomFilterStyle();
    this.params = params;
    this.model = null;
    this.gui = document.createElement('div');
    this.gui.className = 'aptrank-custom-filter';
    this.render();
  }

  render() {
    const minValue = Number.isFinite(this.model?.min) ? this.model.min : '';
    const maxValue = Number.isFinite(this.model?.max) ? this.model.max : '';
    // 설정값이 없으면 슬라이더는 전체 범위(min=좌측끝, max=우측끝)로 배치해 두 핸들이 겹치지 않도록.
    // Number('')는 0(유한값)이라 단순 isFinite 검사로는 빈 값 판별이 안 됨 → 빈 문자열 명시 처리.
    const sliderMin = minValue === '' ? 0 : Math.max(0, Math.min(100, Number(minValue) || 0));
    const sliderMax = maxValue === '' ? 100 : Math.max(0, Math.min(100, Number(maxValue) || 100));

    this.gui.innerHTML = `
      <div class="aptrank-custom-filter-head">
        <div class="aptrank-custom-filter-heading">건수 범위</div>
      </div>
      <div class="aptrank-custom-filter-title">${this.model ? `${minValue} ~ ${maxValue}` : ''}</div>
      <div class="aptrank-custom-filter-range">
        <input class="min" inputmode="numeric" placeholder="최소 (건)" value="${minValue}" />
        <span>~</span>
        <input class="max" inputmode="numeric" placeholder="최대 (건)" value="${maxValue}" />
      </div>
      <div class="aptrank-custom-filter-slider-wrap">
        <div class="aptrank-custom-filter-slider-track"></div>
        <input class="aptrank-custom-filter-slider min-slider" type="range" min="0" max="100" step="1" value="${Math.max(0, Math.min(100, sliderMin))}" />
        <input class="aptrank-custom-filter-slider max-slider" type="range" min="0" max="100" step="1" value="${Math.max(0, Math.min(100, sliderMax))}" />
      </div>
      <div class="aptrank-custom-filter-grid">
        <button class="aptrank-custom-filter-chip" data-range="0-5">~5건</button>
        <button class="aptrank-custom-filter-chip" data-range="5-10">5~10건</button>
        <button class="aptrank-custom-filter-chip" data-range="10-20">10~20건</button>
        <button class="aptrank-custom-filter-chip" data-range="20-30">20~30건</button>
        <button class="aptrank-custom-filter-chip" data-range="30-50">30~50건</button>
        <button class="aptrank-custom-filter-chip" data-range="50-70">50~70건</button>
        <button class="aptrank-custom-filter-chip" data-range="70-100">70~100건</button>
        <button class="aptrank-custom-filter-chip" data-range="100-9999">100건~</button>
      </div>
      <div class="aptrank-custom-filter-actions">
        <button class="aptrank-custom-filter-reset" type="button">초기화</button>
        <button class="aptrank-custom-filter-apply" type="button">적용</button>
      </div>
    `;

    const apply = (rerender = false) => {
      const minRaw = String(this.gui.querySelector('input.min').value || '').replace(/[^\d-]/g, '');
      const maxRaw = String(this.gui.querySelector('input.max').value || '').replace(/[^\d-]/g, '');
      const min = minRaw === '' || minRaw === '-' ? null : Number(minRaw);
      const max = maxRaw === '' || maxRaw === '-' ? null : Number(maxRaw);

      if (!Number.isFinite(min) && !Number.isFinite(max)) {
        this.model = null;
      } else {
        this.model = {
          min: Number.isFinite(min) ? min : Number.NEGATIVE_INFINITY,
          max: Number.isFinite(max) ? max : Number.POSITIVE_INFINITY,
        };
      }
      this.params.filterChangedCallback();
      if (rerender) this.render();
    };

    const updateTrack = () => {
      const minSlider = this.gui.querySelector('input.min-slider');
      const maxSlider = this.gui.querySelector('input.max-slider');
      const track = this.gui.querySelector('.aptrank-custom-filter-slider-track');
      if (!track || !minSlider || !maxSlider) return;

      const minVal = Number(minSlider.value);
      const maxVal = Number(maxSlider.value);
      const leftPercent = (minVal / 100) * 100;
      const rightPercent = 100 - (maxVal / 100) * 100;

      track.style.left = `${leftPercent}%`;
      track.style.right = `${rightPercent}%`;
    };

    // 입력 필드 및 트랙 업데이트, 적용은 버튼으로만
    this.gui.querySelector('input.min-slider').addEventListener('input', (e) => {
      const next = Number(e.target.value);
      const maxText = this.gui.querySelector('input.max').value.replace(/[^\d-]/g, '');
      const max = maxText ? Number(maxText) : 100;
      this.gui.querySelector('input.min').value = String(Math.min(next, max));
      updateTrack();
    });

    this.gui.querySelector('input.max-slider').addEventListener('input', (e) => {
      const next = Number(e.target.value);
      const minText = this.gui.querySelector('input.min').value.replace(/[^\d-]/g, '');
      const min = minText ? Number(minText) : 0;
      this.gui.querySelector('input.max').value = String(Math.max(next, min));
      updateTrack();
    });

    // 초기 트랙 위치 설정
    updateTrack();

    this.gui.querySelectorAll('[data-range]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const [minStr, maxStr] = String(btn.getAttribute('data-range') || '').split('-');
        const min = Number(minStr || 0);
        const max = Number(maxStr || 9999);
        this.gui.querySelector('input.min').value = min;
        this.gui.querySelector('input.max').value = max;
        this.gui.querySelector('input.min-slider').value = Math.min(min, 100);
        this.gui.querySelector('input.max-slider').value = Math.min(max, 100);
        updateTrack();
        this.model = { min, max };
        this.params.filterChangedCallback();
      });
    });

    // 적용 버튼
    this.gui.querySelector('.aptrank-custom-filter-apply').addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      apply(false);
    });

    // 초기화 버튼
    this.gui.querySelector('.aptrank-custom-filter-reset').addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.gui.querySelector('input.min').value = '';
      this.gui.querySelector('input.max').value = '';
      this.gui.querySelector('input.min-slider').value = 0;
      this.gui.querySelector('input.max-slider').value = 100;
      updateTrack();
      this.model = null;
      this.params.filterChangedCallback();
    });
  }

  getGui() {
    return this.gui;
  }

  isFilterActive() {
    return !!this.model;
  }

  doesFilterPass(p) {
    if (!this.model) return true;
    const raw = this.params.valueGetter ? this.params.valueGetter(p.node) : p?.data?.[this.params.colDef?.field];
    const value = Number(raw);
    if (!Number.isFinite(value)) return false;
    return value >= this.model.min && value <= this.model.max;
  }

  getModel() {
    return this.model;
  }

  setModel(model) {
    this.model = model || null;
    if (this.gui) this.render();
  }
}

export default CountRangeFilter;
